import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Package, FileText, Truck, PackageCheck, Clock, Edit, Trash2, AlertTriangle, Loader2, Download, MessageCircle } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';
import { GreyBillForm, LRReportForm, GreyReceiptForm } from '../components/forms';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { exportOrderToPDF } from '../lib/pdf-export';
import { OrderWhatsAppButton } from '../components/ui/whatsapp-button';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { controls } = useSettings();
  const { hasModulePermission } = usePermissions();
  const [order, setOrder] = useState(null);
  const [bills, setBills] = useState([]);
  const [lrs, setLRs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBill, setShowAddBill] = useState(false);
  const [showAddLR, setShowAddLR] = useState(false);
  const [showAddReceipt, setShowAddReceipt] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editLR, setEditLR] = useState(null);
  const [deleteLR, setDeleteLR] = useState(null);
  const [deletingLR, setDeletingLR] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const [orderRes, billsRes, lrsRes, receiptsRes] = await Promise.allSettled([
        api.get(`/grey-orders/${orderId}`),
        api.get(`/grey-bills?order_id=${orderId}`),
        api.get(`/lr-reports?order_id=${orderId}`),
        api.get(`/grey-receipts?order_id=${orderId}`)
      ]);
      if (orderRes.status === 'fulfilled') setOrder(orderRes.value.data);
      setBills(billsRes.status === 'fulfilled' ? billsRes.value.data : []);
      setLRs(lrsRes.status === 'fulfilled' ? lrsRes.value.data : []);
      setReceipts(receiptsRes.status === 'fulfilled' ? receiptsRes.value.data : []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load order details');
      setLoading(false);
    }
  };

  const handleAddSuccess = () => {
    fetchOrderDetails();
    setShowAddBill(false);
    setShowAddLR(false);
    setShowAddReceipt(false);
    setEditLR(null);
  };

  const handleDeleteLR = async () => {
    if (!deleteLR) return;
    setDeletingLR(true);
    try {
      await api.delete(`/lr-reports/${deleteLR.lr_id}`);
      toast.success(`LR ${deleteLR.lr_number} deleted successfully`);
      setDeleteLR(null);
      fetchOrderDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete LR');
    }
    setDeletingLR(false);
  };

  const handleDeleteOrder = async () => {
    setDeleting(true);
    try {
      await api.delete(`/grey-orders/${orderId}`);
      toast.success('Order deleted successfully!');
      navigate('/grey-orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
    setDeleting(false);
    setShowDeleteDialog(false);
  };

  const hasRelatedRecords = bills.length > 0 || lrs.length > 0 || receipts.length > 0;

  // Memoize initialData objects to prevent form re-renders
  const billInitialData = useMemo(() => order ? ({
    order_rate: order.rate, order_unit: order.order_unit,
    order_number: order.order_number, supplier_name: order.supplier_name
  }) : null, [order?.rate, order?.order_unit, order?.order_number, order?.supplier_name]);

  const lrInitialData = useMemo(() => order ? ({
    order_number: order.order_number
  }) : null, [order?.order_number]);

  const receiptInitialData = useMemo(() => order ? ({
    order_number: order.order_number, order_unit: order.order_unit
  }) : null, [order?.order_number, order?.order_unit]);

  const statusColors = {
    'Pending': 'bg-red-50 text-red-700 border-red-200',
    'Partially Billed': 'bg-amber-50 text-amber-700 border-amber-200',
    'Completed': 'bg-teal-50 text-teal-700 border-teal-200'
  };

  const lrStatusColors = {
    'In Transport': 'bg-blue-50 text-blue-700 border-blue-200',
    'Issued for Delivery': 'bg-amber-50 text-amber-700 border-amber-200',
    'Delivered': 'bg-green-50 text-green-700 border-green-200'
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-600">Order not found</p>
      </div>
    );
  }

  return (
    <div data-testid="order-details-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/grey-orders')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-mono" style={{ fontFamily: 'JetBrains Mono' }}>
              {order.order_number}
            </h1>
            <p className="text-slate-600 mt-1">{formatDate(order.order_date)}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={`${statusColors[order.order_status]} text-sm px-3 py-1`}>
              {order.order_status}
            </Badge>
            {/* Order Aging Display */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-lg">
              <Clock className="h-4 w-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-700">
                {order.order_status === 'Completed' ? (
                  <span className="text-green-700">Completed in {order.days_elapsed || 0} days</span>
                ) : (
                  <span className={order.days_elapsed > 30 ? 'text-red-600' : order.days_elapsed > 15 ? 'text-amber-600' : 'text-slate-700'}>
                    Pending {order.days_elapsed || 0} days
                  </span>
                )}
              </span>
            </div>
            {hasModulePermission('grey_orders', 'export_pdf') && controls.enable_pdf_export && (
              <Button 
                variant="outline"
                className="gap-2"
                onClick={() => exportOrderToPDF(order)}
                title="Export as PDF"
                data-testid="order-pdf-btn"
              >
                <Download className="h-4 w-4" />
                PDF
              </Button>
            )}
            {hasModulePermission('grey_orders', 'send_whatsapp') && controls.enable_whatsapp && (
              <OrderWhatsAppButton order={order} supplierPhone={order.supplier_phone} />
            )}
            {hasModulePermission('grey_orders', 'edit') && (
              <Button 
                data-testid="edit-order-btn"
                onClick={() => navigate(`/grey-orders/${orderId}/edit`)}
                variant="outline"
                className="gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
            {hasModulePermission('grey_orders', 'delete') && (
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="delete-order-btn"
                    variant="destructive"
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Order</DialogTitle>
                    </DialogHeader>
                    {hasRelatedRecords ? (
                      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">This order cannot be deleted</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Related records exist: {bills.length > 0 && `${bills.length} Bill(s)`}
                            {bills.length > 0 && (lrs.length > 0 || receipts.length > 0) && ', '}
                            {lrs.length > 0 && `${lrs.length} LR Report(s)`}
                            {lrs.length > 0 && receipts.length > 0 && ', '}
                            {receipts.length > 0 && `${receipts.length} Receipt(s)`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p>Are you sure you want to delete order <strong>{order.order_number}</strong>? This action cannot be undone.</p>
                    )}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                      {!hasRelatedRecords && (
                        <Button variant="destructive" onClick={handleDeleteOrder} disabled={deleting}>
                          {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete Order'}
                        </Button>
                      )}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            <p className="text-xs text-slate-600 mb-1">Ordered</p>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {formatNumber(order.ordered_quantity)}
            </p>
            <p className="text-xs text-slate-500 mt-1">{order.order_unit}</p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            <p className="text-xs text-slate-600 mb-1">Billed</p>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {formatNumber(order.total_billed_quantity)}
            </p>
            <p className="text-xs text-slate-500 mt-1">{order.order_unit}</p>
          </CardContent>
        </Card>

        <Card className="border border-teal-200 shadow-sm bg-teal-50/50">
          <CardContent className="pt-4">
            <p className="text-xs text-teal-700 mb-1">Received</p>
            <p className="text-xl font-bold text-teal-700" style={{ fontFamily: 'Manrope' }}>
              {formatNumber(order.total_received_quantity)}
            </p>
            <p className="text-xs text-teal-600 mt-1">{order.order_unit}</p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            <p className="text-xs text-slate-600 mb-1">Pending Quantity</p>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {formatNumber(order.pending_quantity)}
            </p>
            <p className="text-xs text-slate-500 mt-1">{order.order_unit}</p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            <p className="text-xs text-slate-600 mb-1">Order Value</p>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              ₹{formatNumber(order.order_amount || (order.ordered_quantity * order.rate))}
            </p>
            {order.is_amount_approximate && (
              <p className="text-xs text-amber-600 mt-1 font-medium">≈ Approximately</p>
            )}
            {order.meters_equivalent && (
              <p className="text-xs text-slate-500 mt-1">({formatNumber(order.meters_equivalent)} meters)</p>
            )}
            <p className="text-xs text-slate-500 mt-1">@ ₹{order.rate}/meter</p>
          </CardContent>
        </Card>
      </div>

      {/* Order Info Card */}
      <Card className="border border-slate-200 shadow-sm mb-6">
        <CardHeader>
          <CardTitle>Order Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500 mb-1">Supplier</p>
              <p className="font-medium text-slate-900">{order.supplier_name}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-1">City</p>
              <p className="font-medium text-slate-900">{order.city}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-1">Item</p>
              <p className="font-medium text-slate-900">{order.item || order.item_name || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-1">Quality</p>
              <p className="font-medium text-slate-900">{order.quality_name}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-1">Finish Quality</p>
              <p className="font-medium text-slate-900">{order.subquality_name}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-1">Created By</p>
              <p className="font-medium text-slate-900">{order.created_by_name}</p>
            </div>
            {order.remarks && (
              <div className="md:col-span-2 lg:col-span-3">
                <p className="text-slate-500 mb-1">Remarks</p>
                <p className="font-medium text-slate-900">{order.remarks}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="bills" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="bills" data-testid="bills-tab">
            <FileText className="h-4 w-4 mr-2" />
            Bills ({bills.length})
          </TabsTrigger>
          <TabsTrigger value="lrs" data-testid="lrs-tab">
            <Truck className="h-4 w-4 mr-2" />
            LRs ({lrs.length})
          </TabsTrigger>
          <TabsTrigger value="receipts" data-testid="receipts-tab">
            <PackageCheck className="h-4 w-4 mr-2" />
            Receipts ({receipts.length})
          </TabsTrigger>
        </TabsList>

        {/* Bills Tab */}
        <TabsContent value="bills">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Grey Bills</CardTitle>
              <div className="flex gap-2">
                <Button data-testid="bulk-add-bills-btn" variant="outline" size="sm" onClick={() => navigate(`/bulk/grey-bills/${orderId}`)}>
                  <Plus className="h-4 w-4 mr-2" />Bulk Add
                </Button>
                <Sheet open={showAddBill} onOpenChange={setShowAddBill}>
                <SheetTrigger asChild>
                  <Button data-testid="add-bill-btn" size="sm" className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Bill
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Add Grey Bill</SheetTitle>
                  </SheetHeader>
                  <GreyBillForm 
                    orderId={orderId} 
                    initialData={billInitialData}
                    onSuccess={handleAddSuccess} 
                  />
                </SheetContent>
              </Sheet>
              </div>
            </CardHeader>
            <CardContent>
              {bills.length > 0 ? (
                <div className="space-y-3">
                  {bills.map((bill) => (
                    <div 
                      key={bill.bill_id} 
                      className={`p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors ${
                        bill.rate_mismatch ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'
                      }`}
                      onClick={() => navigate(`/grey-bills/${bill.bill_id}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-slate-900">{bill.bill_number}</span>
                          {bill.rate_mismatch && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Rate Mismatch
                            </Badge>
                          )}
                        </div>
                        <Badge variant="outline">{formatDate(bill.billed_date)}</Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        <div>
                          <p className="text-slate-500">Party:</p>
                          <p className="font-medium">{bill.party_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Quantity:</p>
                          <p className="font-medium">{formatNumber(bill.billed_quantity)} {order.order_unit}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Bill No:</p>
                          <p className="font-medium">{bill.purchase_bill_number}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Order Rate:</p>
                          <p className="font-medium">₹{order.rate}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Bill Rate:</p>
                          <p className={`font-medium ${bill.rate_mismatch ? 'text-amber-700' : ''}`}>
                            {bill.bill_rate ? `₹${bill.bill_rate}` : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Added by:</p>
                          <p className="font-medium">{bill.created_by_name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No bills added yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LRs Tab */}
        <TabsContent value="lrs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>LR Reports</CardTitle>
              <Sheet open={showAddLR} onOpenChange={setShowAddLR}>
                <SheetTrigger asChild>
                  <Button data-testid="add-lr-btn" size="sm" className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="h-4 w-4 mr-2" />
                    Add LR
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Add LR Report</SheetTitle>
                  </SheetHeader>
                  <LRReportForm 
                    orderId={orderId} 
                    bills={bills} 
                    initialData={lrInitialData}
                    onSuccess={handleAddSuccess} 
                  />
                </SheetContent>
              </Sheet>
            </CardHeader>
            <CardContent>
              {lrs.length > 0 ? (
                <div className="space-y-3">
                  {lrs.map((lr) => {
                    const hasReceipts = receipts.some(r => r.lr_id === lr.lr_id);
                    return (
                    <div 
                      key={lr.lr_id} 
                      className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span 
                          className="font-mono font-medium text-slate-900 cursor-pointer hover:text-blue-600"
                          onClick={() => navigate(`/lr-reports/${lr.lr_id}`)}
                        >
                          {lr.lr_number}
                        </span>
                        <div className="flex items-center gap-2">
                          <Badge className={lrStatusColors[lr.status] || 'bg-slate-100 text-slate-700'}>
                            {lr.status}
                          </Badge>
                          <Badge variant="outline">{formatDate(lr.lr_date)}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-slate-500">Transport:</p>
                          <p className="font-medium">{lr.transport_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">LR Number:</p>
                          <p className="font-medium">{lr.lr_number_transport}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Bales:</p>
                          <p className="font-medium">{lr.bales_in_lr}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Created by:</p>
                          <p className="font-medium">{lr.created_by_name}</p>
                        </div>
                      </div>
                      {/* Edit / Delete - only available from Order Details */}
                      {(hasModulePermission('lr_reports', 'edit') || hasModulePermission('lr_reports', 'delete')) && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                          {hasModulePermission('lr_reports', 'edit') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={(e) => { e.stopPropagation(); setEditLR(lr); }}
                            data-testid={`edit-lr-${lr.lr_id}`}
                          >
                            <Edit className="h-3 w-3" />Edit
                          </Button>
                          )}
                          {hasModulePermission('lr_reports', 'delete') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); setDeleteLR(lr); }}
                            data-testid={`delete-lr-${lr.lr_id}`}
                          >
                            <Trash2 className="h-3 w-3" />Delete
                          </Button>
                          )}
                          {hasReceipts && (
                            <span className="text-xs text-slate-400 ml-1">Receipt linked</span>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No LRs added yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipts Tab */}
        <TabsContent value="receipts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Grey Receipts</CardTitle>
              <Sheet open={showAddReceipt} onOpenChange={setShowAddReceipt}>
                <SheetTrigger asChild>
                  <Button data-testid="add-receipt-btn" size="sm" className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Receipt
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Add Grey Receipt</SheetTitle>
                  </SheetHeader>
                  <GreyReceiptForm 
                    orderId={orderId} 
                    orderUnit={order.order_unit}
                    bills={bills} 
                    lrs={lrs}
                    initialData={receiptInitialData}
                    onSuccess={handleAddSuccess} 
                  />
                </SheetContent>
              </Sheet>
            </CardHeader>
            <CardContent>
              {receipts.length > 0 ? (
                <div className="space-y-3">
                  {receipts.map((receipt) => (
                    <div 
                      key={receipt.receipt_id} 
                      className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/grey-receipts/${receipt.receipt_id}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono font-medium text-slate-900">{receipt.receipt_number}</span>
                        <Badge variant="outline">{formatDate(receipt.received_date)}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {receipt.lr_number_transport && (
                          <div>
                            <p className="text-slate-500">LR (Bilty):</p>
                            <p className="font-mono font-medium">{receipt.lr_number_transport} <span className="text-xs text-slate-400">{receipt.lr_status ? `- ${receipt.lr_status}` : ''}</span></p>
                          </div>
                        )}
                        <div>
                          <p className="text-slate-500">Bales Received:</p>
                          <p className="font-medium text-teal-700">{formatNumber(receipt.received_quantity)} Bales</p>
                        </div>
                        {(receipt.received_material_quantity != null || receipt.material_unit) && (
                          <div>
                            <p className="text-slate-500">Material Qty:</p>
                            <p className="font-medium text-teal-700" data-testid="od-receipt-material-qty">
                              {receipt.received_material_quantity != null ? formatNumber(receipt.received_material_quantity) : '—'} {receipt.material_unit || ''}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-slate-500">Verified By:</p>
                          <p className="font-medium">{receipt.verified_by}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Gate Status:</p>
                          <p className="font-medium">{receipt.gate_status}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Added by:</p>
                          <p className="font-medium">{receipt.created_by_name}</p>
                        </div>
                        {receipt.discrepancy_notes && (
                          <div className="col-span-2">
                            <p className="text-slate-500">Discrepancy:</p>
                            <p className="font-medium text-red-600">{receipt.discrepancy_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <PackageCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No receipts added yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit LR Sheet */}
      <Sheet open={!!editLR} onOpenChange={(open) => !open && setEditLR(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit LR Report - {editLR?.lr_number}</SheetTitle>
          </SheetHeader>
          {editLR && (
            <LRReportForm
              orderId={orderId}
              bills={bills}
              initialData={editLR}
              mode="edit"
              hasLinkedReceipts={receipts.some(r => r.lr_id === editLR.lr_id)}
              isAdmin={user?.role === 'admin'}
              onSuccess={handleAddSuccess}
              onCancel={() => setEditLR(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Delete LR Dialog */}
      <Dialog open={!!deleteLR} onOpenChange={(open) => !open && setDeleteLR(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete LR Report
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete <span className="font-mono font-medium">{deleteLR?.lr_number}</span>?
            {receipts.some(r => r.lr_id === deleteLR?.lr_id) && (
              <span className="block mt-2 text-red-600 font-medium">
                This LR has linked receipt(s). Delete the receipt(s) first before deleting this LR.
              </span>
            )}
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteLR(null)} disabled={deletingLR}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteLR} disabled={deletingLR} className="bg-red-600 hover:bg-red-700">
              {deletingLR ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetails;